'use client';

import React, { useState } from 'react';
import { X, FileText, Save, Trash2, Plus, Edit3, ArrowLeft } from 'lucide-react';
import dynamic from 'next/dynamic';

// Dynamically import ReactQuill to avoid SSR issues
const ReactQuill = dynamic(
  () => import('./NotesEditor'),
  { 
    ssr: false,
      loading: () => <div className="h-[200px] bg-[var(--study-surface-soft)] rounded-lg animate-pulse" />
  }
);

interface Note {
  id: number;
  title: string;
  content: string;
  createdAt?: string;
  updatedAt: string;
}

const FloatingNotes = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [notes, setNotes] = useState<Note[]>(() => {
    if (typeof window === 'undefined') return [];
    try {
      const savedNotes = window.localStorage.getItem('userNotes');
      if (!savedNotes) return [];
      const parsed: unknown = JSON.parse(savedNotes);
      return Array.isArray(parsed) ? parsed as Note[] : [];
    } catch (error) {
      console.error('Error parsing saved notes:', error);
      return [];
    }
  });
  const [currentNote, setCurrentNote] = useState<Note | { id: null; title: string; content: string }>({ id: null, title: '', content: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [selectedNoteId, setSelectedNoteId] = useState<number | null>(null);
  const [showMobileView, setShowMobileView] = useState<'list' | 'editor'>('list');


  const saveToLocalStorage = (notesToSave: Note[]) => {
    localStorage.setItem('userNotes', JSON.stringify(notesToSave));
  };

  const openModal = () => {
    setIsModalOpen(true);
    setShowMobileView('list');
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentNote({ id: null, title: '', content: '' });
    setIsEditing(false);
    setSelectedNoteId(null);
    setShowMobileView('list');
  };

  const createNewNote = () => {
    setCurrentNote({ id: null, title: '', content: '' });
    setIsEditing(true);
    setSelectedNoteId(null);
    setShowMobileView('editor');
  };

  const saveNote = () => {
    if (!currentNote.title.trim()) {
      alert('Please enter a title for your note');
      return;
    }

    const now = new Date().toISOString();
    let updatedNotes;

    if (currentNote.id) {
      updatedNotes = notes.map(note =>
        note.id === currentNote.id
          ? { ...currentNote, updatedAt: now }
          : note
      );
    } else {
      const newNote = {
        id: Date.now(),
        title: currentNote.title,
        content: currentNote.content,
        createdAt: now,
        updatedAt: now
      };
      updatedNotes = [...notes, newNote];
    }

    setNotes(updatedNotes);
    saveToLocalStorage(updatedNotes);
    setIsEditing(false);
    setCurrentNote({ id: null, title: '', content: '' });
    setShowMobileView('list');
  };

  const editNote = (note: Note) => {
    setCurrentNote(note);
    setIsEditing(true);
    setSelectedNoteId(note.id);
    setShowMobileView('editor');
  };

  const deleteNote = (noteId: number) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      const updatedNotes = notes.filter(note => note.id !== noteId);
      setNotes(updatedNotes);
      saveToLocalStorage(updatedNotes);
      if (selectedNoteId === noteId) {
        setCurrentNote({ id: null, title: '', content: '' });
        setIsEditing(false);
        setSelectedNoteId(null);
      }
    }
  };

  const selectNote = (note: Note) => {
    setCurrentNote(note);
    setSelectedNoteId(note.id);
    setIsEditing(false);
    setShowMobileView('editor');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'link' 
  ];

  const goBackToList = () => {
    setShowMobileView('list');
    setIsEditing(false);
  };

  return (
    <>
      {/* Floating Notes Button */}
      <div className="study-theme fixed bottom-6 right-6 z-40 rounded-full">
        <button
          onClick={openModal}
          className="study-button-primary p-4 rounded-full shadow-lg transition-[color,background-color,border-color,opacity,transform] duration-300 hover:scale-110 group"
          title="Open Notes"
        >
          <FileText className="w-6 h-6 text-white group-hover:scale-110 transition-transform" />
        </button>
      </div>

      {/* Full Screen Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/45 z-[60] flex items-center justify-center backdrop-blur-sm p-4 md:p-6">
          <div className="practice-notes-modal study-theme study-card w-full max-w-7xl h-[85vh] md:h-[80vh] md:rounded-xl flex flex-col overflow-hidden">
            {/* Header */}
            <div className="study-section-soft border-b border-[var(--study-border)] p-4 md:p-6 flex items-center justify-between md:rounded-t-xl flex-shrink-0">
              <div className="flex items-center">
                {showMobileView === 'editor' && (
                  <button
                    onClick={goBackToList}
                    className="md:hidden study-muted hover:text-[var(--study-primary)] transition-colors p-2 hover:bg-[var(--study-primary-soft)] rounded-full mr-2"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                )}
                <h2 className="study-title text-xl md:text-2xl font-bold">My Notes</h2>
              </div>
              <button
                onClick={closeModal}
                className="study-muted hover:text-[var(--study-primary)] transition-colors p-2 hover:bg-[var(--study-primary-soft)] rounded-full"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 flex overflow-hidden">
              {/* Notes List Sidebar */}
              <div className={`${showMobileView === 'list' ? 'w-full' : 'hidden'} md:w-1/3 md:block study-section-soft border-r border-[var(--study-border)] flex flex-col`}>
                <div className="p-4 border-b border-[var(--study-border)]">
                  <button
                    onClick={createNewNote}
                    className="study-button-primary w-full py-3 px-4 rounded-xl transition-colors flex items-center justify-center space-x-2 font-bold"
                  >
                    <Plus className="w-5 h-5 text-white" />
                    <span>New Note</span>
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-[var(--study-bg-soft)] [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[var(--study-border)] [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-[var(--study-hint)]">
                  {notes.length === 0 ? (
                    <div className="study-muted p-6 text-center">
                      <FileText className="w-12 h-12 mx-auto mb-3" />
                      <p>No notes yet. Create your first note!</p>
                    </div>
                  ) : (
                    <div className="space-y-2 p-4">
                      {notes.map((note) => (
                        <div
                          key={note.id}
                          onClick={() => selectNote(note)}
                          className={`p-4 rounded-xl cursor-pointer transition-colors border ${
                            selectedNoteId === note.id
                              ? 'study-button-primary border-[var(--study-primary)]'
                              : 'study-card hover:border-[var(--study-primary)]'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h3 className={`font-semibold text-sm truncate mb-1 ${
                                selectedNoteId === note.id ? 'text-white' : 'study-title'
                              }`}>
                                {note.title}
                              </h3>
                              <p className={`text-xs ${
                                selectedNoteId === note.id ? 'text-white/80' : 'study-muted'
                              }`}>
                                {formatDate(note.updatedAt)}
                              </p>
                            </div>
                            <div className="flex space-x-1 ml-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  editNote(note);
                                }}
                                className={`p-1 hover:bg-white/20 rounded transition-colors ${
                                  selectedNoteId === note.id ? 'text-white' : 'study-muted hover:text-[var(--study-primary)]'
                                }`}
                              >
                                <Edit3 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteNote(note.id);
                                }}
                                className={`p-1 hover:bg-red-500/20 rounded transition-colors ${
                                  selectedNoteId === note.id ? 'text-white hover:text-red-400' : 'text-red-400 hover:text-red-300'
                                }`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Note Editor/Viewer */}
              <div className={`${showMobileView === 'editor' ? 'w-full' : 'hidden'} md:flex-1 md:flex md:flex-col bg-[var(--study-surface)]`}>
                {isEditing ? (
                  <div className="flex-1 flex flex-col">
                    <div className="p-4 study-section-soft border-b border-[var(--study-border)]">
                      <input
                        type="text"
                        value={currentNote.title}
                        onChange={(e) => setCurrentNote({ ...currentNote, title: e.target.value })}
                        placeholder="Note title..."
                        className="study-search study-input w-full p-3 rounded-xl focus:outline-none text-lg font-semibold"
                      />
                    </div>
                    <div className="flex-1 p-4 overflow-hidden">
                      <ReactQuill
                        value={currentNote.content}
                        onChange={(content) => setCurrentNote({ ...currentNote, content })}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Start writing your note..."
                        className="h-full [&_.ql-editor]:min-h-[200px] [&_.ql-editor]:max-h-[calc(100vh-400px)] [&_.ql-container]:max-h-[calc(100vh-400px)]"
                      />
                    </div>
                    <div className="p-4 study-section-soft border-t border-[var(--study-border)] flex flex-col md:flex-row justify-end space-y-2 md:space-y-0 md:space-x-3">
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          setShowMobileView('list');
                        }}
                        className="study-filter-idle px-6 py-2 rounded-xl transition-colors font-bold"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveNote}
                        className="study-button-primary px-6 py-2 rounded-xl transition-colors flex items-center justify-center space-x-2 font-bold"
                      >
                        <Save className="w-4 h-4 text-white" />
                        <span>Save Note</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col">
                    {selectedNoteId ? (
                      <>
                        <div className="p-4 study-section-soft border-b border-[var(--study-border)]">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h1 className="study-title text-xl md:text-2xl font-bold mb-2">
                                {currentNote.title}
                              </h1>
                              <p className="study-muted text-sm">
                                Last updated: {currentNote.id ? formatDate((currentNote as Note).updatedAt) : 'N/A'}
                              </p>
                            </div>
                            <button
                              onClick={() => editNote(currentNote as unknown as Note)}
                              className="study-link ml-4 p-2 hover:bg-[var(--study-primary-soft)] rounded-full transition-colors"
                            >
                              <Edit3 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                        <div className="flex-1 p-4 overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-[var(--study-bg-soft)] [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[var(--study-border)] [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-[var(--study-hint)]">
                          <div 
                            className="practice-content prose max-w-none"
                            dangerouslySetInnerHTML={{ __html: currentNote.content }}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="study-muted flex-1 flex items-center justify-center">
                        <div className="text-center">
                          <FileText className="w-16 h-16 mx-auto mb-4" />
                          <p className="study-title text-xl mb-2">Select a note to view</p>
                          <p>Choose a note from the sidebar or create a new one</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingNotes;

